import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Auth
import Login from "./pages/auth/Login";

// Admin pages
import AdminDashboard from "./pages/admin/dashboard/Dashboard";
import TourPrograms from "./pages/admin/tours/TourPrograms";
import Voters from "./pages/admin/voters/Voters";
import Masters from "./pages/admin/masters/Masters";
import Grievances from "./pages/admin/grievances/Grievances";
import DevProjects from "./pages/admin/projects/DevProjects";
import Beneficiary from "./pages/admin/beneficiary/Beneficiary";
import Contacts from "./pages/admin/contacts/Contacts";
import Users from "./pages/admin/users/Users";

// Manager pages
import ManagerDashboard from "./pages/manager/dashboard/Dashboard";
import ManagerGrievances from "./pages/manager/grievances/Grievances";

// Staff pages
import StaffDashboard from "./pages/staff/dashboard/Dashboard";
import StaffGrievances from "./pages/staff/grievances/Grievances";

// Layouts
import AdminLayout from "./components/layout/AdminLayout";
import ManagerLayout from "./components/layout/ManagerLayout";
import StaffLayout from "./components/layout/StaffLayout";

// ── Protected Route ───────────────────────────────────────────
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading)
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );

  if (!user) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to="/login" replace />;

  return children;
};

// ── Role Based Redirect ───────────────────────────────────────
const RoleRedirect = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case "Admin":
      return <Navigate to="/admin/dashboard" replace />;
    case "Manager":
      return <Navigate to="/manager/dashboard" replace />;
    case "Staff":
      return <Navigate to="/staff/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// ── App Routes ────────────────────────────────────────────────
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />

      {/* Default redirect based on role */}
      <Route path="/" element={<RoleRedirect />} />

      {/* ── Admin Routes ── */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["Admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="tours" element={<TourPrograms />} />
        <Route path="voters" element={<Voters />} />
        <Route path="grievances" element={<Grievances />} />
        <Route path="projects" element={<DevProjects />} />
        <Route path="beneficiary" element={<Beneficiary />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="users" element={<Users />} />
        <Route path="masters" element={<Masters />} />
      </Route>

      {/* ── Manager Routes ── */}
      <Route
        path="/manager"
        element={
          <ProtectedRoute allowedRoles={["Manager"]}>
            <ManagerLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ManagerDashboard />} />
        <Route path="grievances" element={<ManagerGrievances />} />
      </Route>

      {/* ── Staff Routes ── */}
      <Route
        path="/staff"
        element={
          <ProtectedRoute allowedRoles={["Staff"]}>
            <StaffLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<StaffDashboard />} />
        <Route path="grievances" element={<StaffGrievances />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<RoleRedirect />} />
    </Routes>
  );
};

// ── App ───────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
